import { supabase } from './supabase';

const PK_UNIQUE_VIOLATION = '23505';

/** True when Postgres reports a duplicate primary key on column `id` (not e.g. a unique email index). */
function isPrimaryKeyUniqueViolation(error: {
  code?: string;
  message?: string;
  details?: string | null;
}): boolean {
  if (String(error.code) !== PK_UNIQUE_VIOLATION) return false;
  const msg = (error.message ?? '').toLowerCase();
  const det = (error.details ?? '').toLowerCase();
  return msg.includes('pkey') || det.includes('key (id)=');
}

/**
 * Generates a new ID for a table by finding the highest existing ID and adding 1
 * @param tableName The name of the table to generate an ID for
 * @returns Promise that resolves to the new ID
 */
export const generateNextId = async (tableName: string): Promise<number> => {
  try {
    // Query the database to find the highest ID
    const { data, error } = await supabase
      .from(tableName)
      .select('id')
      .order('id', { ascending: false })
      .limit(1);

    if (error) {
      console.error(`Error fetching highest ID for ${tableName}:`, error);
      throw error;
    }

    // If there are no records, start with ID 1, otherwise add 1 to the highest ID
    const nextId = data.length > 0 ? data[0].id + 1 : 1;
    return nextId;
  } catch (error) {
    console.error(`Failed to generate ID for ${tableName}:`, error);
    throw error;
  }
};

/**
 * Transaction-safe insert with custom ID generation
 * This function ensures no duplicate IDs even with concurrent access
 * 
 * @param tableName The name of the table to insert into
 * @param record The record data to insert (without ID)
 * @returns Promise that resolves to the inserted record
 */
export const insertWithCustomId = async <T extends Record<string, any>>(
  tableName: string,
  record: Omit<T, 'id'>,
  attempt = 0
): Promise<T> => {
  const maxAttempts = 12;
  try {
    const nextId = await generateNextId(tableName);

    const { data, error } = await supabase
      .from(tableName)
      .insert([{ id: nextId, ...record }])
      .select()
      .single();

    if (error) {
      if (attempt < maxAttempts && isPrimaryKeyUniqueViolation(error)) {
        console.warn(`ID collision detected for ${tableName}, retrying with new ID`);
        return insertWithCustomId(tableName, record, attempt + 1);
      }
      console.error(`Error inserting record into ${tableName}:`, error);
      throw error;
    }

    return data as T;
  } catch (error) {
    console.error(`Failed to insert record into ${tableName}:`, error);
    throw error;
  }
};

/**
 * Transaction-safe batch insert with custom ID generation
 * This function ensures no duplicate IDs even with concurrent access
 * 
 * @param tableName The name of the table to insert into
 * @param records An array of record data to insert (without IDs)
 * @returns Promise that resolves to the inserted records
 */
export const batchInsertWithCustomId = async <T extends Record<string, any>>(
  tableName: string, 
  records: Array<Omit<T, 'id'>>
): Promise<T[]> => {
  try {
    if (records.length === 0) {
      return [];
    }

    // Get the highest ID
    const { data, error } = await supabase
      .from(tableName)
      .select('id')
      .order('id', { ascending: false })
      .limit(1);

    if (error) {
      console.error(`Error fetching highest ID for ${tableName}:`, error);
      throw error;
    }

    // Start with ID 1 or increment from the highest existing ID
    let nextId = data.length > 0 ? data[0].id + 1 : 1;
    
    // Add IDs to the records
    const recordsWithIds = records.map(record => ({
      id: nextId++,
      ...record
    }));

    // Insert all records at once
    const { data: insertedData, error: insertError } = await supabase
      .from(tableName)
      .insert(recordsWithIds)
      .select();

    if (insertError) {
      console.error(`Error batch inserting records into ${tableName}:`, insertError);
      throw insertError;
    }

    return insertedData as T[];
  } catch (error) {
    console.error(`Failed to batch insert records into ${tableName}:`, error);
    throw error;
  }
}; 